import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Facebook, UploadCloud, X, ImagePlus, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function FacebookImport() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [pasted, setPasted] = useState("");
  const [form, setForm] = useState({ title: "", description: "", category: "", city: user?.city || "", province: "" });
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/categories").then(({ data }) => setCategories(data)); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const extract = () => {
    const lines = pasted.split("\n").map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) return toast.error("Incolla prima il testo del post");
    const title = lines[0].slice(0, 80);
    const description = pasted.trim();
    setForm((f) => ({ ...f, title, description }));
    toast.success("Testo estratto! Controlla e completa i campi.");
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, data.url]);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore upload");
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description) return toast.error("Estrai o inserisci titolo e descrizione");
    if (!form.category) return toast.error("Seleziona una categoria");
    setSaving(true);
    try {
      await api.post("/listings/import", { ...form, images, source: "facebook" });
      toast.success(user?.role === "admin" ? "Annuncio importato e pubblicato!" : "Annuncio importato! In attesa di moderazione.");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="inline-flex items-center gap-2 rounded-full bg-[#1877F2]/10 border border-[#1877F2]/30 px-4 py-1.5 text-sm font-semibold text-[#1877F2]">
        <Facebook className="h-4 w-4" /> Importa dal gruppo Facebook
      </div>
      <h1 className="font-head font-extrabold text-3xl sm:text-4xl tracking-tight mt-4">Importa un annuncio</h1>
      <p className="text-muted-foreground mt-2">Copia il testo del post dal gruppo, incollalo qui, carica la foto e pubblica sul sito in un clic.</p>

      <form onSubmit={submit} className="mt-8 space-y-6">
        <div className="space-y-2">
          <Label>Testo del post Facebook</Label>
          <Textarea value={pasted} onChange={(e) => setPasted(e.target.value)} placeholder="Incolla qui il testo del post del gruppo..." className="rounded-xl min-h-32" data-testid="fb-paste" />
          <Button type="button" variant="outline" onClick={extract} className="rounded-full" data-testid="fb-extract"><Wand2 className="h-4 w-4 mr-1" /> Estrai titolo e descrizione</Button>
        </div>

        <div>
          <Label>Foto (scarica dal post e caricale qui)</Label>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-2">
            {images.map((img, i) => (
              <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-border">
                <img src={mediaUrl(img)} alt="" className="h-full w-full object-cover" />
                <button type="button" onClick={() => setImages(images.filter((_, x) => x !== i))} className="absolute top-1 right-1 bg-black/60 rounded-full p-1" data-testid={`fb-remove-img-${i}`}><X className="h-3 w-3 text-white" /></button>
              </div>
            ))}
            <label className="aspect-square rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors text-muted-foreground" data-testid="fb-upload-label">
              {uploading ? <UploadCloud className="h-6 w-6 animate-pulse" /> : <ImagePlus className="h-6 w-6" />}
              <span className="text-xs mt-1">Aggiungi</span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={onUpload} data-testid="fb-upload-input" />
            </label>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="t">Titolo</Label>
          <Input id="t" value={form.title} onChange={set("title")} required data-testid="fb-title" placeholder="Es. Divano 2 posti" className="h-11 rounded-xl" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="d">Descrizione</Label>
          <Textarea id="d" value={form.description} onChange={set("description")} required data-testid="fb-desc" className="rounded-xl min-h-28" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Categoria</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="h-11 rounded-xl" data-testid="fb-category"><SelectValue placeholder="Scegli" /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label htmlFor="c">Città</Label><Input id="c" value={form.city} onChange={set("city")} required data-testid="fb-city" placeholder="Milano" className="h-11 rounded-xl" /></div>
          <div className="space-y-2"><Label htmlFor="p">Provincia</Label><Input id="p" value={form.province} onChange={set("province")} data-testid="fb-province" placeholder="MI" className="h-11 rounded-xl" /></div>
        </div>

        <Button type="submit" disabled={saving || !user?.email_verified} data-testid="fb-submit" className="w-full h-12 rounded-full text-base">
          {saving ? "Importazione..." : "Importa e pubblica"}
        </Button>
      </form>
    </div>
  );
}
